import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Copy,
  Check, ExternalLink, Phone, MapPin, Loader2, Info, X,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths,
  isSameMonth, isSameDay, format,
} from "date-fns";
import { fr } from "date-fns/locale";

const KIND_STYLE = {
  EMS: "bg-fegreen text-white",
  Massage: "bg-amber-400 text-white",
};

export default function AdminPlanning() {
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [icsUrl, setIcsUrl] = useState("");
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch a wide window for the cursor month
  useEffect(() => {
    setLoading(true);
    const from = format(addDays(startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), -7), "yyyy-MM-dd");
    const to = format(addDays(endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }), 7), "yyyy-MM-dd");
    api.get(`/admin/planning?date_from=${from}&date_to=${to}`)
      .then((r) => setEvents(r.data.events || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [cursor]);

  useEffect(() => {
    api.get("/admin/planning/ics-link")
      .then((r) => setIcsUrl(r.data.url))
      .catch(() => {});
  }, []);

  const days = useMemo(() => buildCalendarGrid(cursor), [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      (map[ev.date] = map[ev.date] || []).push(ev);
    }
    return map;
  }, [events]);

  const eventsForSelected = selectedDay
    ? (eventsByDay[format(selectedDay, "yyyy-MM-dd")] || [])
    : [];

  const copyIcs = async () => {
    try {
      await navigator.clipboard.writeText(icsUrl);
      setCopied(true);
      toast.success("Lien iCal copié !");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Impossible de copier — sélectionnez le lien manuellement");
    }
  };

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="flex flex-wrap justify-between items-end gap-4 mb-8">
        <div>
          <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-2">— Agenda</p>
          <h1 className="text-3xl lg:text-4xl font-bold text-fegray-dark">Planning</h1>
          <p className="text-sm text-fegray mt-2">Toutes vos séances payées, prêtes à être synchronisées avec Google Agenda.</p>
        </div>
        <button
          onClick={() => setShowSyncModal(true)}
          data-testid="planning-sync-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-fegreen text-white text-xs font-bold tracking-wide uppercase hover:bg-fegreen-dark transition-colors shadow-md"
        >
          <CalendarIcon className="w-4 h-4" /> Synchroniser avec Google
        </button>
      </div>

      {/* Calendar header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-fegray-dark capitalize" data-testid="planning-month-label">
          {format(cursor, "MMMM yyyy", { locale: fr })}
        </h2>
        <div className="flex items-center gap-1">
          <NavBtn onClick={() => setCursor(addMonths(cursor, -1))} icon={ChevronLeft} testid="planning-prev" />
          <button
            onClick={() => setCursor(new Date())}
            data-testid="planning-today"
            className="px-3 py-1.5 rounded-full border border-gray-200 text-xs font-bold text-fegray-dark hover:border-fegreen hover:text-fegreen"
          >
            Aujourd'hui
          </button>
          <NavBtn onClick={() => setCursor(addMonths(cursor, 1))} icon={ChevronRight} testid="planning-next" />
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] uppercase tracking-wider text-fegray font-bold text-center">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
          <div key={d} className="py-2">{d}</div>
        ))}
      </div>

      {loading && events.length === 0 ? (
        <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-fegreen mx-auto" /></div>
      ) : (
        <div className="grid grid-cols-7 gap-1" data-testid="planning-grid">
          {days.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const dayEvents = eventsByDay[key] || [];
            const isCurMonth = isSameMonth(day, cursor);
            const isToday = isSameDay(day, new Date());
            return (
              <button
                key={key}
                onClick={() => setSelectedDay(day)}
                data-testid={`planning-day-${key}`}
                className={`relative min-h-[110px] p-2 rounded-xl text-left border transition-all ${
                  isCurMonth ? "bg-white border-gray-100" : "bg-fegray-light/30 border-transparent"
                } ${isToday ? "ring-2 ring-fegreen" : ""} hover:border-fegreen hover:shadow-sm`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-bold ${
                    isCurMonth ? "text-fegray-dark" : "text-fegray/40"
                  } ${isToday ? "text-fegreen" : ""}`}>
                    {format(day, "d")}
                  </span>
                  {dayEvents.length > 0 && (
                    <span className="text-[9px] font-extrabold bg-fegreen-light text-fegreen rounded-full px-1.5">
                      {dayEvents.length}
                    </span>
                  )}
                </div>
                <div className="mt-1 space-y-1">
                  {dayEvents.slice(0, 3).map((ev) => (
                    <div
                      key={ev.id}
                      className={`text-[10px] truncate rounded px-1.5 py-0.5 font-semibold ${
                        KIND_STYLE[ev.kind] || "bg-fegray text-white"
                      }`}
                    >
                      {ev.time} {ev.client_name || "Client"}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[9px] text-fegray font-bold pl-1">+{dayEvents.length - 3} de plus</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-6 text-xs text-fegray">
        <Legend dotClass="bg-fegreen" label="EMS" />
        <Legend dotClass="bg-amber-400" label="Massage / Drainage" />
        <span className="ml-auto inline-flex items-center gap-1.5">
          <Info className="w-3 h-3" /> Seules les réservations <strong className="text-fegray-dark">payées</strong> sont affichées.
        </span>
      </div>

      {/* Day detail panel */}
      {selectedDay && (
        <DayDetailPanel
          day={selectedDay}
          events={eventsForSelected}
          onClose={() => setSelectedDay(null)}
        />
      )}

      {/* Sync modal */}
      {showSyncModal && (
        <SyncModal
          icsUrl={icsUrl}
          copied={copied}
          onCopy={copyIcs}
          onClose={() => setShowSyncModal(false)}
        />
      )}
    </div>
  );
}

function NavBtn({ onClick, icon: Icon, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-9 h-9 rounded-full border border-gray-200 text-fegray-dark flex items-center justify-center hover:border-fegreen hover:text-fegreen transition-colors"
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

function Legend({ dotClass, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${dotClass}`} />
      {label}
    </span>
  );
}

function DayDetailPanel({ day, events, onClose }) {
  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[420px] bg-white shadow-2xl border-l border-gray-100 z-50 overflow-y-auto animate-fade-in" data-testid="planning-day-panel">
      <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-fegreen font-bold">Journée</p>
          <h3 className="text-lg font-bold text-fegray-dark capitalize">
            {format(day, "EEEE d MMMM yyyy", { locale: fr })}
          </h3>
        </div>
        <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="planning-day-close">
          <X className="w-4 h-4 text-fegray-dark" />
        </button>
      </div>

      <div className="p-5">
        {events.length === 0 ? (
          <p className="text-sm text-fegray text-center py-12">Aucune séance ce jour.</p>
        ) : (
          <div className="space-y-3">
            {events.map((ev) => (
              <div key={ev.id} className="fe-card p-4" data-testid={`planning-event-${ev.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="text-2xl font-extrabold text-fegray-dark">{ev.time}</div>
                    <div className="text-[10px] uppercase tracking-wider text-fegray">{ev.duration_min} min</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-1 ${
                    KIND_STYLE[ev.kind] || "bg-fegray text-white"
                  }`}>
                    {ev.kind}
                  </span>
                </div>
                <div className="font-bold text-fegray-dark">{ev.client_name}</div>
                <div className="text-xs text-fegreen mt-0.5">{ev.pack_name}</div>
                {ev.telephone && (
                  <a href={`tel:${ev.telephone}`} className="text-xs text-fegray-dark inline-flex items-center gap-1.5 mt-2 hover:text-fegreen">
                    <Phone className="w-3 h-3" /> {ev.telephone}
                  </a>
                )}
                {ev.address && (
                  <div className="text-xs text-fegray inline-flex items-start gap-1.5 mt-1">
                    <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" /> {ev.address}
                  </div>
                )}
                {ev.client_id && (
                  <Link
                    to={`/admin/clients/${ev.client_id}`}
                    className="text-xs text-fegreen font-semibold hover:underline mt-2 inline-block"
                  >
                    Voir la fiche client →
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SyncModal({ icsUrl, copied, onCopy, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      data-testid="planning-sync-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-7 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="planning-sync-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
        >
          <X className="w-4 h-4 text-fegray-dark" />
        </button>
        <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-3">— Synchronisation</p>
        <h3 className="text-2xl font-extrabold text-fegray-dark mb-2">Google Agenda</h3>
        <p className="text-sm text-fegray mb-5 leading-relaxed">
          Copiez le lien ci-dessous, puis dans <strong>Google Agenda</strong> → Paramètres → <strong>Ajouter un agenda</strong> → <strong>À partir de l'URL</strong>, collez le lien. Vos séances payées apparaîtront automatiquement et seront actualisées toutes les heures.
        </p>

        <div className="flex items-center gap-2 mb-4">
          <input
            type="text"
            value={icsUrl}
            readOnly
            data-testid="planning-ics-input"
            className="flex-1 bg-fegray-light border border-gray-200 rounded-xl px-3 py-2.5 text-xs text-fegray-dark font-mono truncate focus:outline-none"
            onFocus={(e) => e.target.select()}
          />
          <button
            onClick={onCopy}
            data-testid="planning-ics-copy"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-fegreen text-white text-xs font-bold hover:bg-fegreen-dark transition-colors"
          >
            {copied ? <><Check className="w-3.5 h-3.5" /> Copié</> : <><Copy className="w-3.5 h-3.5" /> Copier</>}
          </button>
        </div>

        <a
          href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="planning-open-google"
          className="inline-flex items-center gap-2 text-fegreen font-semibold hover:underline text-sm"
        >
          Ouvrir Google Agenda <ExternalLink className="w-3.5 h-3.5" />
        </a>

        <div className="mt-5 pt-5 border-t border-gray-100 text-[11px] text-fegray leading-relaxed">
          <p className="mb-1"><strong className="text-fegray-dark">Lecture seule.</strong> Les rendez-vous saisis directement dans Google Agenda ne sont pas remontés dans le pod.</p>
          <p>Compatible également avec Apple Calendar, Outlook et tout client supportant iCal/ICS.</p>
        </div>
      </div>
    </div>
  );
}

function buildCalendarGrid(cursor) {
  const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
  const days = [];
  let d = start;
  while (d <= end) {
    days.push(d);
    d = addDays(d, 1);
  }
  return days;
}
